import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Clock, CheckCircle, AlertTriangle, Crown } from 'lucide-react';

export default function SubscriptionBanner() {
  const { subscriptionStatus, user } = useAuth();
  const navigate = useNavigate();


  if (!subscriptionStatus) {
    console.log('[SubscriptionBanner] No subscription status, returning null');
    return null;
  }

  const {
    role,
    subscription_status,
    trial_days_remaining,
    is_trial_active,
    subscription_days_remaining,
    is_subscription_active
  } = subscriptionStatus;

  // Don't show banner for admins
  if (role === 'admin' || role === 'superuser') return null;

  // Active subscription - show renewal reminder if 10 days or less remaining
  if (subscription_status === 'active' && is_subscription_active && subscription_days_remaining <= 10) {
    const isUrgent = subscription_days_remaining <= 3;
    
    return (
      <div className={`${
        isUrgent ? 'bg-orange-50 border-orange-200' : 'bg-purple-50 border-purple-200'
      } border-b px-4 py-3 sm:px-6`}>
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <Crown className={`w-5 h-5 ${isUrgent ? 'text-orange-600' : 'text-purple-600'}`} />
            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
              <span className={`font-semibold ${isUrgent ? 'text-orange-900' : 'text-purple-900'}`}>
                Your subscription expires in {subscription_days_remaining} day{subscription_days_remaining !== 1 ? 's' : ''}
              </span>
              <span className={`text-sm ${isUrgent ? 'text-orange-700' : 'text-purple-700'}`}>
                Renew now to continue enjoying La VidaLex
              </span>
            </div>
          </div>
          <button 
            onClick={() => navigate('/subscribe')}
            className={`${
              isUrgent 
                ? 'bg-orange-600 hover:bg-orange-700' 
                : 'bg-purple-600 hover:bg-purple-700'
            } text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors hidden sm:block`}
          >
            Renew Now
          </button>
        </div>
      </div>
    );
  }

  // Don't show banner for active subscribers with more than 10 days remaining
  if (subscription_status === 'active' && is_subscription_active) {
    return null;
  }

  // Trial user - show remaining days
  if (subscription_status === 'trial' && is_trial_active) {
    const isUrgent = trial_days_remaining <= 3;
    
    return (
      <div className={`${
        isUrgent ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'
      } border-b px-4 py-3 sm:px-6`}>
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <Clock className={`w-5 h-5 ${isUrgent ? 'text-amber-600' : 'text-blue-600'}`} />
            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
              <span className={`font-semibold ${isUrgent ? 'text-amber-900' : 'text-blue-900'}`}>
                {trial_days_remaining} day{trial_days_remaining !== 1 ? 's' : ''} remaining in your free trial
              </span>
              {isUrgent && (
                <span className="text-sm text-amber-700">
                  Subscribe now to continue using La VidaLex
                </span>
              )}
            </div>
          </div>
          <button 
            onClick={() => navigate('/subscribe')}
            className={`${
              isUrgent 
                ? 'bg-amber-600 hover:bg-amber-700' 
                : 'bg-blue-600 hover:bg-blue-700'
            } text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors hidden sm:block`}
          >
            Subscribe Now
          </button>
        </div>
      </div>
    );
  }

  // Expired subscription or trial
  if (subscription_status === 'expired' || !is_trial_active) {
    return (
      <div className="bg-red-50 border-b border-red-200 px-4 py-3 sm:px-6">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <span className="font-semibold text-red-900">
              Your trial has expired. Subscribe to continue using La VidaLex.
            </span>
          </div>
          <button 
            onClick={() => navigate('/subscribe')}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors hidden sm:block"
          >
            Subscribe Now
          </button>
        </div>
      </div>
    );
  }

  return null;
}
